import { QueryKey } from '@/configs/queryKeys';
import { Product } from './ProductApi';
import { User } from '@/types/user';
import { UUID } from '@/types/types';
import { fileTypeToHeader } from '@/utils/utilities';
import EntityApi, { BaseEntity } from './EntityApi';
import httpClient from './httpClient';

export enum OrderStatus {
	NOT_SENT = 'NOT_SENT',
	SENT = 'SENT',
	CONFIRMED = 'CONFIRMED',
	COMPLETED = 'COMPLETED',
}

export enum TransportType {
	EWAN = 'ewan',
	COURIER = 'courier',
	SELF_PICKUP = 'self-pickup',
}

export interface OrderShippingData {
	shippingName: string;
	shippingStreet: string;
	shippingCity: string;
	shippingPostalCode: string;
	shippingPhoneNumber: string;
	shippingDate: Date;
	shippingTransportType: TransportType;
}

export interface Order extends BaseEntity, OrderShippingData {
	number: number;
	name: string;
	user: User;
	isArchived: boolean;
	description: string;
	status: OrderStatus;
	products: Product[];
	totalPrice: number;
	totalDiscountedPrice: number;
}

class OrderApi extends EntityApi<Order> {
	constructor() {
		super(QueryKey.orders);
		this.submit = this.submit.bind(this);
		this.generatePdf = this.generatePdf.bind(this);
		this.generateOfferPdf = this.generateOfferPdf.bind(this);
		this.copy = this.copy.bind(this);
		this.archive = this.archive.bind(this);
	}

	async submit({
		orderId,
		shippingData,
	}: {
		orderId: UUID;
		shippingData: OrderShippingData;
	}) {
		const response = await httpClient.patch<Order>(
			`${this.queryKey}/${orderId}/submit`,
			shippingData
		);
		return response.data;
	}

	async generatePdf({ order }: { order: Order }) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${order.id}/export`,
			{
				headers: {
					Accept: fileTypeToHeader('pdf'),
				},
				responseType: 'blob',
			}
		);

		return response.data;
	}

	async generateOfferPdf({ order, margin }: { order: Order; margin: number }) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${order.id}/export-offer`,
			{
				params: { margin },
				headers: {
					Accept: fileTypeToHeader('pdf'),
				},
				responseType: 'blob',
			}
		);

		return response.data;
	}

	async copy({ orderId }: { orderId: UUID }) {
		const response = await httpClient.post<Order>(`${this.queryKey}/copy`, {
			orderId,
		});

		return response.data;
	}

	async archive({ orderId }: { orderId: UUID }) {
		const response = await httpClient.patch<Order>(
			`${this.queryKey}/${orderId}/archive`,
			{
				isArchived: true,
			}
		);
		return response.data;
	}
}

export default new OrderApi();
